import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/services/api';
import { useAdminNotifications } from '@/context/AdminNotificationContext';
import { motion } from 'framer-motion';
import { 
  Activity,
  UserPlus,
  Copy,
  ArrowDownToLine,
  ArrowUpFromLine,
  Clock,
  Filter,
  RefreshCw,
  Wifi,
  WifiOff,
  Mail,
  DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

const EVENT_CONFIG = {
  new_user_signup: {
    label: 'New User Signup',
    icon: UserPlus,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-400/10',
    borderColor: 'border-cyan-400/30',
  },
  wallet_address_copied: {
    label: 'Address Copied',
    icon: Copy,
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10',
    borderColor: 'border-purple-400/30',
  },
  deposit_submitted: {
    label: 'Deposit Submitted',
    icon: ArrowDownToLine,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400/10',
    borderColor: 'border-emerald-400/30',
  },
  withdrawal_requested: {
    label: 'Withdrawal Request',
    icon: ArrowUpFromLine,
    color: 'text-hotpink-400',
    bgColor: 'bg-hotpink-400/10',
    borderColor: 'border-hotpink-400/30',
  },
};

export default function ActivityFeedPage() {
  const { recentEvents, wsConnected, clearUnreadCount } = useAdminNotifications();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchEvents();
    clearUnreadCount();
  }, [clearUnreadCount]);

  // Merge recent WebSocket events with fetched events
  useEffect(() => {
    if (recentEvents.length > 0) {
      setEvents(prev => {
        // Remove duplicates based on event ID
        const existingIds = new Set(prev.map(e => e.id));
        const newEvents = recentEvents.filter(e => !existingIds.has(e.id));
        return [...newEvents, ...prev];
      });
    }
  }, [recentEvents]);

  const fetchEvents = async () => {
    try {
      const params = {};
      if (filterType !== 'all') {
        params.event_type = filterType;
      }
      params.limit = 100;
      
      const response = await adminAPI.getActivityFeed(params);
      setEvents(response.data.events || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchEvents();
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setLoading(true);
    setTimeout(() => fetchEvents(), 100);
  };

  const filteredEvents = filterType === 'all' 
    ? events 
    : events.filter(e => e.event_type === filterType);

  const renderEventCard = (event) => {
    const config = EVENT_CONFIG[event.event_type] || {
      label: event.event_type,
      icon: Activity,
      color: 'text-white',
      bgColor: 'bg-white/10',
      borderColor: 'border-white/30',
    };
    const Icon = config.icon;

    return (
      <motion.div
        key={event.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className={`glass-card p-4 border ${config.borderColor} ${config.bgColor}`}
        data-testid={`activity-event-${event.id}`}
      >
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center shrink-0`}>
            <Icon className={`w-5 h-5 ${config.color}`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className={`text-sm font-semibold ${config.color}`}>
                {config.label}
              </span>
              <span className="text-xs text-white/40 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {format(new Date(event.created_at), 'MMM dd, HH:mm:ss')}
              </span>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-white font-medium text-sm">{event.user_name || 'Unknown User'}</span>
              <span className="text-white/40 text-xs flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {event.user_email}
              </span>
            </div>

            {/* Event Details */}
            <div className="text-sm text-white/60">
              {event.event_type === 'new_user_signup' && event.details && (
                <span>From {event.details.country || 'Unknown'}</span>
              )}
              {event.event_type === 'wallet_address_copied' && (
                <span>Copied <span className="text-cyan-400">{event.crypto_type}</span> wallet address</span>
              )}
              {event.event_type === 'deposit_submitted' && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  <span className="text-emerald-400 font-mono font-semibold">
                    ${event.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="text-white/40">{event.crypto_type}</span>
                </span>
              )}
              {event.event_type === 'withdrawal_requested' && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  <span className="text-hotpink-400 font-mono font-semibold">
                    ${event.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="text-white/40">{event.crypto_type}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-hotpink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-6"
      data-testid="activity-feed-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-white flex items-center gap-3">
            <Activity className="w-8 h-8 text-cyan-400" />
            Activity Feed
          </h1>
          <p className="text-white/60 mt-1">Real-time user activity notifications</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection Status */}
          {wsConnected ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <Wifi className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">Live</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <WifiOff className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-medium">Reconnecting</span>
            </div>
          )}
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-feed-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      {stats && (
        <motion.div variants={fadeInUp} className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="glass-card p-4">
            <p className="text-white/50 text-xs uppercase tracking-wide">Total Events</p>
            <p className="text-2xl font-bold text-white mt-1">{stats.total_events?.toLocaleString() || 0}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-white/50 text-xs uppercase tracking-wide">Today</p>
            <p className="text-2xl font-bold text-cyan-400 mt-1">{stats.events_today?.toLocaleString() || 0}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-white/50 text-xs uppercase tracking-wide">Signups</p>
            <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.by_type?.new_user_signup || 0}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-white/50 text-xs uppercase tracking-wide">Deposits</p>
            <p className="text-2xl font-bold text-purple-400 mt-1">{stats.by_type?.deposit_submitted || 0}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-white/50 text-xs uppercase tracking-wide">Withdrawals</p>
            <p className="text-2xl font-bold text-hotpink-400 mt-1">{stats.by_type?.withdrawal_requested || 0}</p>
          </div>
        </motion.div>
      )}

      {/* Filter */}
      <motion.div variants={fadeInUp} className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-white/50" />
        <Select value={filterType} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-48 bg-white/5 border-white/10" data-testid="event-type-filter">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent className="bg-space-100 border-white/10">
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="new_user_signup">New Signups</SelectItem>
            <SelectItem value="wallet_address_copied">Address Copied</SelectItem>
            <SelectItem value="deposit_submitted">Deposits</SelectItem>
            <SelectItem value="withdrawal_requested">Withdrawals</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-white/40 text-sm">
          Showing {filteredEvents.length} events
        </span>
      </motion.div>

      {/* Events List */}
      <motion.div variants={fadeInUp} className="space-y-3">
        {filteredEvents.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Activity className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">No activity events yet</p>
            <p className="text-white/30 text-sm mt-1">
              Events will appear here when users sign up, copy wallet addresses, or submit transactions
            </p>
          </div>
        ) : (
          filteredEvents.map(event => renderEventCard(event))
        )}
      </motion.div>
    </motion.div>
  );
}
